import { useRef, useState } from "react";
import FavoritedMeList from "@/components/FavoritedMeList";
import FavoritesList from "@/components/FavoritesList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUnreads } from "@/tanstack-hooks";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FavoriteSortBy } from "@/generated-api/models/FavoriteSortBy";
import { ListStartIcon, MonitorIcon } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useStandaloneMode } from "@/hooks/useStandaloneMode";

export default function FavoritesPage() {
  const [sorting, setSorting] = useState(() => {
    const savedTab = sessionStorage.getItem("favoritesSorting");
    return savedTab === FavoriteSortBy.LAST_FAVORITED ||
      savedTab === FavoriteSortBy.RECENTLY_ACTIVE
      ? savedTab
      : FavoriteSortBy.LAST_FAVORITED;
  });
  const unreadsQuery = useUnreads();
  const selectTriggerRef = useRef<HTMLButtonElement>(null);
  const isStandalone = useStandaloneMode();
  const t = useTranslation();

  const unreads = unreadsQuery.data;
  // Initialize state synchronously
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = sessionStorage.getItem("favoritesActiveTab");
    return savedTab === "favorites" || savedTab === "favorited-me"
      ? savedTab
      : "favorites";
  });

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    sessionStorage.setItem("favoritesActiveTab", value);
  };

  function handleSorting(newSorting: FavoriteSortBy) {
    setSorting(newSorting);
    sessionStorage.setItem("favoritesSorting", newSorting);
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={handleTabChange}
      className={`dark flex flex-col w-full items-center text-center select-none ${
        isStandalone ? "pb-14" : "pb-10"
      }`}
    >
      <div className="flex flex-wrap w-full max-w-5xl md:justify-between items-center gap-2">
        <TabsList className="w-full md:w-fit grid grid-cols-2 rounded-xl">
          <TabsTrigger value="favorites" className="rounded-lg">
            {t.FAVORITES_PAGE.LABELS.FAVORITES}
          </TabsTrigger>
          <TabsTrigger value="favorited-me" className="items-center rounded-lg">
            {t.FAVORITES_PAGE.LABELS.FAVORITED_ME}
            {(unreads?.favorite_unreads
              ? unreads.favorite_unreads > 0
              : false) && (
              <div className="flex-shrink-0 w-[0.58rem] h-[0.58rem] bg-[#CF0202] rounded-full ml-2" />
            )}
          </TabsTrigger>
        </TabsList>
        <div className="w-full md:w-fit items-center">
          <Select onValueChange={handleSorting} value={sorting}>
            <SelectTrigger
              className="rounded-xl gap-2 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              ref={selectTriggerRef}
            >
              <SelectValue placeholder="Select sorting" />
            </SelectTrigger>
            <SelectContent className="dark rounded-xl">
              <SelectGroup>
                <SelectItem
                  value={FavoriteSortBy.LAST_FAVORITED}
                  className="py-3 rounded-lg"
                >
                  <span className="flex items-center gap-3">
                    <ListStartIcon className="w-4 h-4" aria-hidden="true" />
                    {t.FAVORITES_PAGE.LABELS.LAST_FAVORITED}
                  </span>
                </SelectItem>
                <SelectItem
                  className="py-3 rounded-lg"
                  value={FavoriteSortBy.RECENTLY_ACTIVE}
                >
                  <span className="flex items-center gap-3">
                    <MonitorIcon className="w-4 h-4" aria-hidden="true" />
                    {t.FAVORITES_PAGE.LABELS.RECENTLY_ACTIVE}
                  </span>
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
      <TabsContent value="favorites" className="w-full max-w-5xl">
        <FavoritesList key={sorting} sortBy={sorting} />
      </TabsContent>
      <TabsContent value="favorited-me" className="w-full max-w-5xl">
        <FavoritedMeList key={sorting} sortBy={sorting} />
      </TabsContent>
    </Tabs>
  );
}
